import { createMuiTheme } from "@material-ui/core";

import { IColors } from "../models/Colors";
import { BentoTheme, ThemeName, WeVideoTheme } from "../models/WeVideoTheme";
import {
	ColorTokensDarkTheme as bentoDark,
	ElevationDarkTheme as elevationDark,
} from "../tokens/colors/ColorTokens";
import { applyOpacity } from "../utils/colors";
import { DARK_NEUTRALS, DEFAULT_BORDER_RADIUS, DEFAULT_SPACING, TYPERAMP } from "../utils/const";
import { getBentoTheme, getWeVideoTheme } from "../utils/parsers";

const NEUTRALS = DARK_NEUTRALS;

const colors: IColors = {
	layer: {
		1: {
			rest: NEUTRALS.N700,
			hover: NEUTRALS.N600,
		},
	},
	button: {
		primary: {
			text: {
				rest: NEUTRALS.N000,
				hover: NEUTRALS.N000,
				disabled: NEUTRALS.N000,
			},
			container: {
				rest: NEUTRALS.BRAND.PRIMARY,
				hover: NEUTRALS.BRAND.SHADE200,
				disabled: NEUTRALS.BRAND.SHADE250,
			},
		},
		secondary: {
			text: {
				rest: NEUTRALS.N000,
				hover: NEUTRALS.N000,
				disabled: NEUTRALS.N300,
			},
			container: {
				rest: NEUTRALS.N010,
				hover: NEUTRALS.N022,
				disabled: NEUTRALS.N006,
			},
		},
		tertiary: {
			text: {
				rest: NEUTRALS.N000,
				hover: NEUTRALS.N000,
				disabled: NEUTRALS.N300,
			},
			container: {
				rest: NEUTRALS.GREEN.PRIMARY,
				hover: NEUTRALS.GREEN.SHADE100,
				disabled: NEUTRALS.GREEN.SHADE200,
			},
		},
		textButton: {
			primary: {
				rest: NEUTRALS.BRAND.TINT100,
				disabled: NEUTRALS.BRAND.TINT150,
			},
			tertiary: {
				rest: NEUTRALS.GREEN.PRIMARY,
				disabled: NEUTRALS.GREEN.SHADE200,
			},
		},
		// TODO Fix and move radio in form: {...}
		radio: {
			outline: {
				selected: NEUTRALS.BRAND.PRIMARY,
				disabled: NEUTRALS.N300,
				notSelected: NEUTRALS.N820,
			},
			fill: {
				selected: NEUTRALS.BRAND.PRIMARY,
				disabled: NEUTRALS.N300,
				notSelected: NEUTRALS.N100,
			},
			hover: {
				selected: NEUTRALS.BRAND.SHADE200,
				disabled: "",
				notSelected: NEUTRALS.N010,
			},
		},
	},
	form: {
		checkbox: {
			outline: {
				fill: "transparent",
				stroke: NEUTRALS.N100,
			},
			outlineDisabled: {
				fill: "transparent",
				stroke: NEUTRALS.N300,
			},
			hover: {
				fill: NEUTRALS.BRAND.SHADE200,
				stroke: NEUTRALS.N010,
			},
		},
	},
	controls: {
		scrollbar: NEUTRALS.N025,
	},
	divider: NEUTRALS.N010,
	elevation: {
		one: "0px 2px 3px rgba(0, 0, 0, 0.18)",
		two: "0px 1px 6px rgba(0, 0, 0, 0.06), 0px 6px 12px rgba(0, 0, 0, 0.12)",
		three:
			"0px 8px 17px rgba(0, 0, 0, 0.1), 0px 1px 6px rgba(0, 0, 0, 0.06), 0px 6px 12px rgba(0, 0, 0, 0.06)",
	},
	error: NEUTRALS.RED.PRIMARY,
	graphics: {
		fill: NEUTRALS.GRAY.TINT250,
	},
	outline: NEUTRALS.N000,
	icon: {
		primary: NEUTRALS.N100,
		disabled: NEUTRALS.N300,
		error: NEUTRALS.RED.PRIMARY,
	},
	input: {
		box: {
			border: NEUTRALS.N300,
			active: NEUTRALS.BRAND.PRIMARY,
			disabled: NEUTRALS.N200,
			error: NEUTRALS.RED.PRIMARY,
			fill: NEUTRALS.N820,
		},
	},

	knob: NEUTRALS.N000,
	presence: {
		zero: "#037CB0",
		one: "#BB5202",
		two: "#887615",
		three: "#BB2E02",
		four: "#12865C",
		five: "#007C84",
		six: "#0C8043",
		seven: "#B24545",
		eight: "#8B02BB",
		nine: "#545454",
		ten: "#8389CC",
		eleven: "#38AAAF",
	},
	states: {
		hover: applyOpacity(NEUTRALS.N000, 8),
		selected: NEUTRALS.N100,
	},
	rows: {
		rest: NEUTRALS.N700,
		hover: applyOpacity(NEUTRALS.N000, 8),
		selected: applyOpacity(NEUTRALS.N000, 12),
		hoverSelected: applyOpacity(`${NEUTRALS.N000}`, 16),
	},
	slider: {
		value: NEUTRALS.BRAND.PRIMARY,
		max: NEUTRALS.N010,
		marker: NEUTRALS.N100,
		rail: NEUTRALS.N010,
	},
	stepper: {
		button: NEUTRALS.N400,
	},
	surface: {
		mainBg: NEUTRALS.N600,
		secondaryBg: NEUTRALS.N400,
		tertiaryBg: NEUTRALS.YELLOW.TINT100,
		one: NEUTRALS.N500,
		two: NEUTRALS.N400,
		three: NEUTRALS.N650,
		four: NEUTRALS.N1006,
		input: NEUTRALS.N700,
		overlay: `${NEUTRALS.N700}CC`, // CC => 80% opacity
	},
	surfaces: NEUTRALS.N750,
	tab: {
		text: {
			selected: NEUTRALS.N000,
			hover: NEUTRALS.N100,
			notSelected: NEUTRALS.N100,
		},
		underline: {
			selected: NEUTRALS.BRAND.PRIMARY,
			hover: NEUTRALS.N300,
		},
	},
	pill: {
		text: NEUTRALS.N000,
		textSelected: NEUTRALS.N000,
		background: NEUTRALS.GRAY.TINT500,
		backgroundSelected: NEUTRALS.N010,
		backgroundHover: NEUTRALS.N022,
		border: NEUTRALS.N300,
		gradient: NEUTRALS.N600,
		icon: NEUTRALS.N100,
		disabled: NEUTRALS.N300,
	},
	logo: {
		primary: NEUTRALS.N000,
		secondary: NEUTRALS.N000,
		tertiary: NEUTRALS.N000,
	},
	weVideoLogo: {
		primary: NEUTRALS.BRAND.SECONDARY,
		secondary: NEUTRALS.N000,
		tertiary: NEUTRALS.N000,
		grayScale: {
			primary: NEUTRALS.N000,
			secondary: NEUTRALS.N700,
		},
	},
	text: {
		primary: NEUTRALS.N000,
		secondary: NEUTRALS.N100,
		tertiary: NEUTRALS.N200,
		disabled: NEUTRALS.N300,
		misc: NEUTRALS.N100,
		description: NEUTRALS.N100,
		selected: NEUTRALS.N700,
		error: NEUTRALS.RED.PRIMARY,
	},
	toggle: {
		selected: {
			border: NEUTRALS.N300,
			fill: NEUTRALS.BRAND.PRIMARY,
		},
		notSelected: {
			border: NEUTRALS.N300,
			fill: NEUTRALS.N400,
		},
	},
	trimmer: {
		transitionBorder: NEUTRALS.BRAND.TINT200,
		transitionFill: NEUTRALS.BRAND.TINT250,
		trimmedZoneFill: applyOpacity(`${NEUTRALS.N650}`, 80),
		trimZone: NEUTRALS.N000,
		trimmerHandleLine: NEUTRALS.N750,
	},
	transitionHint: {
		outerBackground: NEUTRALS.N500,
		background: NEUTRALS.N500,
		outline: NEUTRALS.N200,
		cross: NEUTRALS.N000,
	},
	essentials: {
		red: {
			primary: NEUTRALS.RED.PRIMARY,
			tint100: NEUTRALS.RED.TINT100,
		},
		brand: {
			primary: NEUTRALS.BRAND.PRIMARY,
			tint100: NEUTRALS.BRAND.TINT100,
			tint200: NEUTRALS.BRAND.TINT200,
			shade100: NEUTRALS.BRAND.SHADE100,
			shade200: NEUTRALS.BRAND.SHADE200,
			shade250: NEUTRALS.BRAND.SHADE250,
		},
		gray: {
			tint50: NEUTRALS.GRAY.TINT50,
			tint250: NEUTRALS.GRAY.TINT250,
			tint300: NEUTRALS.GRAY.TINT300,
			tint400: NEUTRALS.GRAY.TINT400,
			tint500: NEUTRALS.GRAY.TINT500,
			tint550: NEUTRALS.GRAY.TINT550,
			tint625: NEUTRALS.GRAY.TINT625,
			dark: NEUTRALS.GRAY.DARK,
			tint04: NEUTRALS.N004,
			chevronGrey: NEUTRALS.N100,
		},
		green: {
			primary: NEUTRALS.GREEN.PRIMARY,
			tint100: NEUTRALS.GREEN.TINT100,
			shade100: NEUTRALS.GREEN.SHADE100,
			shade200: NEUTRALS.GREEN.SHADE200,
		},
		yellow: {
			primary: NEUTRALS.YELLOW.PRIMARY,
			tint100: NEUTRALS.YELLOW.TINT100,
			tint200: NEUTRALS.YELLOW.TINT200,
		},
		white: {
			primary: NEUTRALS.N000,
			tint50: NEUTRALS.N050,
		},
		black: {
			primary: NEUTRALS.N1000,
		},
		purple: {
			primary: NEUTRALS.PURPLE.PRIMARY,
		},
	},
	dashboardCards: {
		classroom: NEUTRALS.TEMPORARY.CLASSROOM_BLUE,
		classic: NEUTRALS.TEMPORARY.CLASSIC_GRAY,
		admin: NEUTRALS.TEMPORARY.ADMIN_YELLOW,
		superAdmin: NEUTRALS.TEMPORARY.ADMIN_YELLOW,
		playPosit: NEUTRALS.TEMPORARY.PLAYPOSIT_BLUE,
	},
	progress: { bar: NEUTRALS.BRAND.PRIMARY, track: NEUTRALS.N300 },
	loader: NEUTRALS.N400,
	appSwitcher: {
		icon: NEUTRALS.N000,
	},
	waveform: {
		backgroundGradientStart: NEUTRALS.GRAY.TINT625,
		backgroundGradientEnd: NEUTRALS.N500,
		stroke: NEUTRALS.GRAY.TINT250,
		fill: NEUTRALS.GRAY.TINT300,
	},
	studentView: {
		color: NEUTRALS.N750,
	},
	space: {
		nextgen: NEUTRALS.BRAND.PRIMARY,
		classic: NEUTRALS.N100,
		interactivity: "#8B02BB",
		admin: "#B68854",
	},
	toaster: {
		info: NEUTRALS.N100,
		success: NEUTRALS.GREEN.TINT100,
		error: NEUTRALS.RED.PRIMARY,
	},
	link: {
		primary: NEUTRALS.BRAND.TINT100,
		hover: NEUTRALS.BRAND.TINT100,
		visited: NEUTRALS.PURPLE.TINT100,
	},
	tooltip: {
		background: NEUTRALS.N750,
	},
};

const weVideoTheme: WeVideoTheme = getWeVideoTheme(colors);
const bentoTheme: BentoTheme = getBentoTheme(bentoDark, elevationDark);

export const DarkTheme = createMuiTheme({
	name: ThemeName.DARK,
	spacing: DEFAULT_SPACING,
	shape: { borderRadius: DEFAULT_BORDER_RADIUS },
	palette: {
		type: "dark",
		// Brand
		primary: {
			main: colors.button.primary.container?.rest!,
			light: colors.button.primary.container?.hover,
			contrastText: colors.button.primary.text.rest,
		},
		secondary: {
			main: colors.button.secondary.container?.rest!,
			light: colors.button.secondary.container?.hover,
			contrastText: colors.button.secondary.text.rest,
		},
		background: {
			default: colors.surface.mainBg,
		},
		text: {
			primary: colors.text.primary,
			secondary: colors.text.secondary,
			disabled: colors.text.disabled,
		},
		divider: colors.divider,
		error: { main: colors.error },
		common: {
			black: colors.essentials.black.primary,
			white: colors.essentials.white.primary,
		},
	},
	deprecatedTheme: weVideoTheme,
	bento: bentoTheme,
	typography: {
		fontFamily: "Lato, lato",
		h1: TYPERAMP.B1_TITLE,
		h2: TYPERAMP.B2_HEADING,
		h3: TYPERAMP.B3_SUBHEAD,
		body1: TYPERAMP.B4_BODY,
		caption: TYPERAMP.B5_CAPTION,
		body2: TYPERAMP.B6_MICRO,
		subtitle1: TYPERAMP.B6_CAPTION_BOLD,
	},

	overrides: {
		MuiPaper: {
			root: {
				backgroundColor: colors.surface.mainBg,
				"&.MuiPaper-search": {
					display: "flex",
					boxShadow: "none",
					border: `1px solid ${colors.divider}`,
					borderRadius: 3,
					height: 32,
					width: 270,
				},
			},
		},
		MuiInputBase: {
			root: {
				width: "100%",
				"&.MuiInputBase-search": {
					...TYPERAMP.B4_BODY,
				},
			},
		},
		MuiButton: {
			root: {
				...TYPERAMP.B4_BODY,
				paddingTop: 4,
				paddingBottom: 4,
				paddingLeft: 16,
				paddingRight: 16,
				height: 32,
				minWidth: 88,
				borderRadius: 16,
				textTransform: "initial",
				"&.Mui-disabled": {
					cursor: "not-allowed",
					pointerEvents: "all",
				},
			},
			contained: {
				boxShadow: "",
				"&:hover": {
					boxShadow: "",
				},
				"&:active": {
					boxShadow: "",
				},
				"&.Mui-tertiary": {
					backgroundColor: colors.button.tertiary.container?.rest,
					color: colors.button.tertiary.text.rest,
					"&:hover": {
						backgroundColor: colors.button.tertiary.container?.hover,
					},
					"&:active": {
						backgroundColor: colors.button.tertiary.container?.hover,
					},
					"&.Mui-disabled": {
						backgroundColor: colors.button.tertiary.container?.disabled,
						color: colors.button.tertiary.text.disabled,
					},
				},
			},
			containedPrimary: {
				"&.Mui-disabled": {
					backgroundColor: colors.button.primary.container?.disabled,
					color: colors.button.primary.text.disabled,
				},
				"&:hover": {
					backgroundColor: colors.essentials.brand.shade200,
					"&.Mui-disabled": {
						backgroundColor: colors.button.primary.container?.disabled,
						color: colors.button.primary.text.disabled,
					},
				},
			},
			containedSecondary: {
				backgroundColor: colors.button.secondary.container?.rest,
				color: colors.button.secondary.text.rest,
				"&:hover": {
					backgroundColor: colors.button.secondary.container?.hover,
					color: colors.button.secondary.text.hover,
					"&.Mui-disabled": {
						backgroundColor: colors.button.secondary.container?.disabled,
						color: colors.button.secondary.text.disabled,
					},
				},
				"&:active": {
					backgroundColor: colors.button.secondary.container?.hover,
				},
				"&.Mui-accent": {
					backgroundColor: colors.button.secondary.container?.rest,
					color: colors.button.secondary.text.rest,
					"&:hover": {
						backgroundColor: colors.button.secondary.container?.hover,
					},
					"&:active": {
						backgroundColor: colors.button.secondary.container?.hover,
					},
					"&.Mui-disabled": {
						backgroundColor: colors.button.secondary.container?.disabled,
						color: colors.button.secondary.text.disabled,
					},
				},
				"&.Mui-disabled": {
					backgroundColor: colors.button.secondary.container?.disabled,
					color: colors.button.secondary.text.disabled,
				},
			},
			text: {
				padding: 4,
				minWidth: 0,
				"&.Mui-tertiary": {
					color: colors.button.textButton.tertiary.rest,
					backgroundColor: "unset",
					"&:hover": {
						color: colors.button.textButton.tertiary.rest,
						backgroundColor: "unset",
						textDecoration: "underline",
					},
					"&.Mui-disabled": {
						color: colors.button.textButton.tertiary.disabled,
					},
				},
				"&.Mui-primary": {
					color: colors.button.textButton.primary.rest,
					backgroundColor: "unset",
					"&:hover": {
						color: colors.button.textButton.primary.rest,
						backgroundColor: "unset",
					},
					"&.Mui-disabled": {
						color: colors.button.textButton.primary.disabled,
					},
				},
			},
			textPrimary: {
				"&:hover": {
					backgroundColor: "unset",
				},
				"&.Mui-disabled": {
					color: colors.button.primary.container?.disabled,
				},
			},
		},
		MuiIconButton: {
			root: {
				color: bentoTheme.colors.Icon.primary,
				"&:hover": {
					backgroundColor: colors.button.secondary.container?.hover,
				},
				"&:active": {
					backgroundColor: colors.button.secondary.container?.hover,
				},
				"&.MuiIconButton-open": {
					backgroundColor: colors.button.secondary.container?.hover,
				},
				"&.MuiIconButton-close": {
					"&:hover": {
						//TODO replace after hexOpacity function is merged
						backgroundColor: `${colors.button.secondary.text.rest}19`,
					},
				},
				"& .MuiIconButton-Text": {
					color: colors.text.primary,
					fontSize: 16,
				},
			},
		},
		MuiMenu: {
			paper: {
				backgroundColor: colors.surface.one,
				borderRadius: 4,
				boxShadow: "0 1 6 rgba(0, 0, 0, 0.06)",
			},
			list: {
				paddingTop: 5,
				paddingBottom: 5,
			},
		},
		MuiMenuItem: {
			root: {
				minWidth: 112,
				"&:hover": {
					backgroundColor: colors.rows.hover,
				},
				"&:active": {
					backgroundColor: colors.rows.hover,
				},
				"&.MuiMenuItem-parent": {
					paddingRight: 8,
				},
			},
		},
		MuiBreadcrumbs: {
			root: {
				...TYPERAMP.B5_CAPTION,
				"& $ol": {},
			},
			li: {
				fontSize: "13px !important",
				color: `${colors.text.secondary} !important`,
				"& div": {
					"& a": {
						color: "inherit",
					},
				},
			},
		},
		MuiSvgIcon: {
			root: {
				fontSize: "16px",
				lineHeight: "16px",
				"&.MuiIcon-small": {
					fontSize: "8px",
					lineHeight: "8px",
				},
				"&.MuiIcon-large": {
					fontSize: "24px",
					lineHeight: "24px",
				},
				"&.MuiIcon-x-large": {
					fontSize: "40px",
					lineHeight: "40px",
				},
			},
		},
		MuiFormLabel: {
			root: {
				paddingBottom: 4,
			},
		},
		MuiCssBaseline: {
			"@global": {
				".sr-only": {
					border: "0 !important",
					clip: "rect(1px, 1px, 1px, 1px) !important",
					width: "1px !important",
					height: "1px !important",
					margin: "-1px !important",
					padding: "0 !important",
					overflow: "hidden !important",
					clipPath: "inset(50%) !important",
					position: "absolute",
					whiteSpace: "nowrap",
				},
			},
		},
	},
	props: {
		MuiButtonBase: {
			disableRipple: true,
		},
		MuiMenu: {
			// In order to make the screen reader correctly read the number of items in the menu we need to have the menu initialized.
			// By setting the transitionDuration to 0 we make sure the menu is initialized immediately.
			transitionDuration: 0,
		},
	},
});
